import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Row, Col, Button, Table, Form, Badge, InputGroup, Spinner } from 'react-bootstrap';
import {
    FaPlus, FaWhatsapp, FaShoppingCart, FaTrash, FaSave, FaSearch,
    FaFileInvoiceDollar, FaChevronRight, FaCheckCircle, FaPaperPlane
} from 'react-icons/fa';
import { fetchRFQs, createRFQ, updateRFQ, updateRFQStatus, deleteRFQ } from '../../redux/features/rfq.features';
import { fetchSuppliers } from '../../redux/features/supplier.features';
import { fetchCompanies } from '../../redux/features/company.features';
import { getAllItemsWithStock } from '../../redux/features/stock.features';

import SupplierField from '../core/shared/fields/SupplierField';
import ItemField from '../core/shared/fields/ItemField'; // Reusing ItemField (assuming it exists or similar)
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utility';
import { sendWhatsApp, WhatsAppFormatter } from '../../utility/WhatsAppFormatter';
import { toast } from 'react-toastify';
import moment from 'moment';

const BRAND_COLOR = '#001c3a';
const SOURCING_ACCENT = '#6f42c1'; // Purple/Indigo

const RFQManager = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Global State
    const { rfqs, status } = useSelector(state => state.rfqReducer);
    const { suppliers } = useSelector(state => state.supplierReducer);
    const { stocks: items } = useSelector(state => state.stockReducer);
    const { companies } = useSelector(state => state.companyReducer);

    // Local State
    const [selectedRFQ, setSelectedRFQ] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isEditing, setIsEditing] = useState(false);

    // Editor State
    const [editorSupplier, setEditorSupplier] = useState(null);
    const [editorAgent, setEditorAgent] = useState(null);
    const [editorAgentName, setEditorAgentName] = useState('');
    const [editorAgentPhone, setEditorAgentPhone] = useState('');
    const [editorItems, setEditorItems] = useState([]);
    const [editorNotes, setEditorNotes] = useState('');

    // Rapid Entry Refs
    const itemInputRef = useRef(null);

    useEffect(() => {
        dispatch(fetchRFQs());
        dispatch(fetchSuppliers());
        dispatch(fetchCompanies());
        dispatch(getAllItemsWithStock({ includeEmptyBase: true }));
    }, [dispatch]);

    // Handle RFQ Selection
    const handleSelectRFQ = (rfq) => {
        setSelectedRFQ(rfq);
        setIsEditing(false);
        setEditorSupplier(rfq.supplier?._id || rfq.supplier || null);
        setEditorAgent(rfq.agent || null);
        setEditorAgentName(rfq.agentName || '');

        // Find phone from supplier contacts if available
        const sup = suppliers.find(s => s._id === (rfq.supplier?._id || rfq.supplier));
        const agentId = rfq.agent?._id || rfq.agent;
        const agentContact = sup?.contacts?.find(c => c._id === agentId);
        setEditorAgentPhone(agentContact?.phone || sup?.contact_info?.contact_number || '');

        setEditorItems(rfq.items || []);
        setEditorNotes(rfq.notes || '');
    };

    const handleNewRFQ = () => {
        const newItem = {
            item_id: null,
            itemName: '',
            variant_id: null,
            qty: 1,
            targetPrice: 0,
            notes: ''
        };

        setSelectedRFQ({ _id: 'new', rfqNumber: 'New RFQ', status: 'Draft' });
        setIsEditing(true);
        setEditorSupplier(null);
        setEditorAgent(null);
        setEditorAgentName('');
        setEditorAgentPhone('');
        setEditorItems([newItem]);
        setEditorNotes('');
    };

    const handleAddItem = () => {
        setEditorItems([...editorItems, {
            item_id: null, itemName: '', variant_id: null, qty: 1, targetPrice: 0, notes: ''
        }]);
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...editorItems];

        if (field === 'multi') {
            newItems[index] = { ...newItems[index], ...value };
        } else {
            newItems[index][field] = value;
        }

        setEditorItems(newItems);
    };

    const handleRemoveItem = (index) => {
        const newItems = editorItems.filter((_, i) => i !== index);
        setEditorItems(newItems);
    };

    const handleSave = async () => {
        // Find supplier name for snapshot
        const supObj = suppliers.find(s => s._id === editorSupplier);

        const payload = {
            supplier: editorSupplier,
            supplierName: supObj?.business_name || 'Generic / Open Market',
            agent: editorAgent || null,
            agentName: editorAgentName,
            items: editorItems.filter(i => i.itemName), // Filter empty rows
            notes: editorNotes,
            status: 'Draft'
        };

        if (selectedRFQ._id === 'new') {
            const res = await dispatch(createRFQ(payload));
            if (!res.error) {
                toast.success('RFQ Created');
                handleSelectRFQ(res.payload);
            }
        } else {
            const res = await dispatch(updateRFQ({ id: selectedRFQ._id, data: payload }));
            if (!res.error) {
                toast.success('RFQ Updated');
                setIsEditing(false);
            }
        }
    };

    const handleWhatsApp = () => {
        if (!selectedRFQ) return;

        const companyProfile = (companies && companies.length > 0) ? companies[0] : null;

        // Dynamically resolve the most accurate contact number
        const supId = editorSupplier || (selectedRFQ.supplier?._id || selectedRFQ.supplier);
        const supObj = suppliers.find(s => s._id === supId);

        const agentId = editorAgent || (selectedRFQ.agent?._id || selectedRFQ.agent);
        const agentObj = supObj?.contacts?.find(c => c._id === agentId);

        // STRIKE PRIORITY: Agent Phone > Supplier Phone
        const contactNo = agentObj?.phone || supObj?.contact_info?.contact_number || '';

        // Use Enterprise Formatter
        const message = WhatsAppFormatter.generateRFQ(selectedRFQ, companyProfile);
        sendWhatsApp(contactNo, message);

        // Update status to Sent
        if (selectedRFQ.status === 'Draft') {
            dispatch(updateRFQStatus({ id: selectedRFQ._id, status: 'Sent' }));
            setSelectedRFQ(prev => ({ ...prev, status: 'Sent' }));
        }
    };

    const handleConvertToPO = async () => {
        // 1. Prepare items for Purchase Form
        const poItems = editorItems.map(it => {
            const catalogItem = items.find(item => item._id === it.item_id);
            const selling = catalogItem?.pricing?.sellingPrice || catalogItem?.lastSellingPrice || 0;
            const isSer = catalogItem?.isSerialized || catalogItem?.category === 'Device' || false;

            return {
                item_id: it.item_id, // Might be null for free text
                variant_id: it.variant_id,
                itemName: it.itemName,
                purchaseQty: it.qty,
                unitCost: it.targetPrice,
                sellingPrice: selling,
                total_price: it.qty * it.targetPrice,
                condition: 'Brand New',
                isSerialized: isSer,
                variantAttributes: catalogItem?.variantAttributes || [],
                serializedItems: isSer ? Array(it.qty).fill({
                    serialNumber: "", unitCost: it.targetPrice, sellingPrice: selling, condition: "Brand New"
                }) : []
            };
        });

        // 2. Save to Session
        sessionStorage.setItem('restock_prefill', JSON.stringify(poItems));

        // 3. Mark Converted
        await dispatch(updateRFQStatus({ id: selectedRFQ._id, status: 'Converted' }));

        // 4. Navigate
        navigate(`/purchases/new?type=po&mode=restock&supplierId=${editorSupplier || ''}`);
    };

    const getStatusVariant = (st) => {
        switch (st) {
            case 'Draft': return 'secondary';
            case 'Sent': return 'primary';
            case 'Converted': return 'success';
            case 'Closed': return 'dark';
            default: return 'light';
        }
    };

    return (
        <Row className="h-100 g-0">
            {/* LEFT SIDEBAR: LIST */}
            <Col md={3} className="border-end bg-white d-flex flex-column h-100" style={{ maxHeight: 'calc(100vh - 100px)' }}>
                <div className="p-3 border-bottom">
                    <Button
                        variant="primary"
                        className="w-100 mb-3 fw-bold shadow-sm"
                        style={{ backgroundColor: SOURCING_ACCENT, borderColor: SOURCING_ACCENT }}
                        onClick={handleNewRFQ}
                    >
                        <FaPlus /> New RFQ
                    </Button>
                    <InputGroup>
                        <InputGroup.Text className="bg-light border-end-0"><FaSearch className="text-muted" /></InputGroup.Text>
                        <Form.Control
                            placeholder="Search RFQs..."
                            className="border-start-0 bg-light"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </InputGroup>
                </div>

                <div className="overflow-auto flex-grow-1">
                    {rfqs.filter(r => r.rfqNumber.toLowerCase().includes(searchTerm.toLowerCase())).map(rfq => (
                        <div
                            key={rfq._id}
                            className={`p-3 border-bottom cursor-pointer hover-bg-light ${selectedRFQ?._id === rfq._id ? 'bg-light border-start border-5' : ''}`}
                            style={{ borderLeftColor: selectedRFQ?._id === rfq._id ? SOURCING_ACCENT : 'transparent' }}
                            onClick={() => handleSelectRFQ(rfq)}
                        >
                            <div className="d-flex justify-content-between mb-1">
                                <strong className="text-dark small">{rfq.rfqNumber}</strong>
                                <Badge bg={getStatusVariant(rfq.status)} className="rounded-pill" style={{ fontSize: '0.65rem' }}>{rfq.status}</Badge>
                            </div>
                            <div className="text-truncate small fw-bold text-muted mb-1">
                                {rfq.supplier?.business_name || rfq.supplierName || 'No Supplier'}
                            </div>
                            <div className="d-flex justify-content-between small text-muted">
                                <span>{rfq.items?.length} Items</span>
                                <span>{moment(rfq.createdAt).format('MMM D')}</span>
                            </div>
                        </div>
                    ))}
                    {rfqs.length === 0 && status !== 'loading' && (
                        <div className="text-center p-4 text-muted small">No RFQs found. Create one!</div>
                    )}
                </div>
            </Col>

            {/* RIGHT CONTENT: EDITOR */}
            <Col md={9} className="h-100 d-flex flex-column bg-light">
                {selectedRFQ ? (
                    <>
                        {/* HEADER */}
                        <div className="bg-white border-bottom p-3 d-flex justify-content-between align-items-center shadow-sm">
                            <div className="d-flex align-items-center gap-3">
                                <div>
                                    <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                        <span style={{ color: SOURCING_ACCENT }}><FaFileInvoiceDollar /> {selectedRFQ.rfqNumber}</span>
                                        {isEditing && <Badge bg="warning" text="dark" className="rounded-pill small">Editing</Badge>}
                                    </h5>
                                    {/* VISUAL VERIFICATION OF CONTACT */}
                                    {!isEditing && (
                                        <div className="x-small text-success fw-bold mt-1">
                                            <FaWhatsapp className="me-1" />
                                            Target: {(() => {
                                                const sup = suppliers.find(s => s._id === (selectedRFQ.supplier?._id || selectedRFQ.supplier));
                                                const agId = selectedRFQ.agent?._id || selectedRFQ.agent;
                                                const ag = sup?.contacts?.find(c => c._id === agId);
                                                return ag ? `${ag.name} (${ag.phone})` : `${sup?.business_name} (${sup?.contact_info?.contact_number})`;
                                            })()}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="d-flex gap-2">
                                {!isEditing ? (
                                    <>
                                        <Button variant="outline-danger" size="sm" onClick={async () => {
                                            if (window.confirm('Are you sure you want to delete this RFQ?')) {
                                                const res = await dispatch(deleteRFQ(selectedRFQ._id));
                                                if (!res.error) {
                                                    toast.success('RFQ Deleted');
                                                    setSelectedRFQ(null);
                                                }
                                            }
                                        }}>
                                            <FaTrash /> Delete
                                        </Button>
                                        <Button variant="outline-success" size="sm" onClick={handleWhatsApp}>
                                            <FaWhatsapp /> Send Inquiry
                                        </Button>
                                        <Button variant="outline-primary" size="sm" onClick={handleConvertToPO} disabled={selectedRFQ.status === 'Converted'}>
                                            <FaShoppingCart /> Convert to PO
                                        </Button>
                                        <Button variant="light" size="sm" onClick={() => setIsEditing(true)}>
                                            <FaSave className="text-secondary" /> Edit
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button variant="light" size="sm" onClick={() => { setIsEditing(false); handleSelectRFQ(selectedRFQ?._id === 'new' ? rfqs[0] : selectedRFQ) }}>Cancel</Button>
                                        <Button variant="primary" size="sm" onClick={handleSave} style={{ backgroundColor: SOURCING_ACCENT, borderColor: SOURCING_ACCENT }}>
                                            <FaSave /> Save Changes
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* BODY */}
                        <div className="p-4 overflow-auto flex-grow-1">
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body>
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Label className="small fw-bold text-muted">Supplier (Optional)</Form.Label>
                                            {isEditing ? (
                                                <div className="d-flex flex-column gap-2">
                                                    <SupplierField
                                                        suppliers={suppliers}
                                                        selectedSupplierId={editorSupplier}
                                                        onAddSupplier={(id) => {
                                                            setEditorSupplier(id);
                                                            setEditorAgent(null);
                                                            setEditorAgentName('');
                                                            setEditorAgentPhone(''); // Reset agent phone when supplier changes
                                                        }}
                                                    />
                                                    {editorSupplier && suppliers.find(s => s._id === editorSupplier)?.contacts?.length > 0 && (
                                                        <Form.Select
                                                            size="sm"
                                                            value={editorAgent || ''}
                                                            onChange={(e) => {
                                                                const s = suppliers.find(s => s._id === editorSupplier);
                                                                const contact = s.contacts.find(c => c._id === e.target.value);
                                                                setEditorAgent(e.target.value);
                                                                setEditorAgentName(contact?.name || '');
                                                                setEditorAgentPhone(contact?.phone || s.contact_info?.contact_number || '');
                                                            }}
                                                        >
                                                            <option value="">-- Select Agent --</option>
                                                            {suppliers.find(s => s._id === editorSupplier).contacts.map(c => (
                                                                <option key={c._id} value={c._id}>{c.name} ({c.phone || 'No Phone'})</option>
                                                            ))}
                                                        </Form.Select>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="d-flex flex-column">
                                                    <div className="fw-bold text-dark fs-5">
                                                        {suppliers.find(s => s._id === editorSupplier)?.business_name || selectedRFQ.supplierName || 'Generic / Open Market'}
                                                    </div>
                                                    {editorAgentName && (
                                                        <div className="small text-primary fw-bold text-uppercase">
                                                            via {editorAgentName}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </Col>
                                        <Col md={6}>
                                            <Form.Label className="small fw-bold text-muted">Status</Form.Label>
                                            <div><Badge bg={getStatusVariant(selectedRFQ.status)} className="px-3 py-2">{selectedRFQ.status}</Badge></div>
                                        </Col>
                                    </Row>

                                    <Form.Label className="small fw-bold text-muted">Internal Notes</Form.Label>
                                    {isEditing ? (
                                        <Form.Control as="textarea" rows={2} value={editorNotes} onChange={(e) => setEditorNotes(e.target.value)} placeholder="Delivery terms, special instructions..." />
                                    ) : (
                                        <div className="bg-light p-2 rounded small text-muted fst-italic">{editorNotes || 'No notes provided.'}</div>
                                    )}
                                </Card.Body>
                            </Card>

                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white fw-bold py-3 small text-uppercase text-muted border-bottom-0">
                                    Items Requested
                                </Card.Header>
                                <Table hover responsive className="mb-0 align-middle">
                                    <thead className="bg-light text-muted small text-uppercase">
                                        <tr>
                                            <th style={{ width: '50px' }}>#</th>
                                            <th>Item Details</th>
                                            <th style={{ width: '120px' }}>Qty</th>
                                            <th style={{ width: '150px' }}>Target Price</th>
                                            {isEditing && <th style={{ width: '50px' }}></th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {editorItems.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="text-center text-muted">{idx + 1}</td>
                                                <td>
                                                    {isEditing ? (
                                                        <ItemField
                                                            items={items}
                                                            defaultInputValue={item.itemName}
                                                            onAddItem={(selected) => {
                                                                // Handle selection from search
                                                                const updates = {
                                                                    item_id: selected._id || null,
                                                                    variant_id: selected.variant_id || null,
                                                                    itemName: selected.itemName,
                                                                    targetPrice: selected.lastUnitCost || selected.costPrice || 0
                                                                };
                                                                handleItemChange(idx, 'multi', updates);
                                                            }}
                                                        />
                                                    ) : (
                                                        <span className="fw-bold text-dark">{item.itemName}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {isEditing ? (
                                                        <Form.Control
                                                            type="number"
                                                            min="1"
                                                            value={item.qty}
                                                            onChange={(e) => handleItemChange(idx, 'qty', parseInt(e.target.value) || 1)}
                                                            className="text-center"
                                                        />
                                                    ) : (
                                                        <span className="badge bg-light text-dark border px-3">{item.qty}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {isEditing ? (
                                                        <InputGroup size="sm">
                                                            <InputGroup.Text className="bg-light border-end-0 text-muted small">LKR</InputGroup.Text>
                                                            <Form.Control
                                                                type="number"
                                                                value={item.targetPrice}
                                                                onChange={(e) => handleItemChange(idx, 'targetPrice', parseFloat(e.target.value) || 0)}
                                                                className="border-start-0 text-end"
                                                            />
                                                        </InputGroup>
                                                    ) : (
                                                        <div className="text-end text-muted small">
                                                            {item.targetPrice > 0 ? formatCurrency(item.targetPrice) : '-'}
                                                        </div>
                                                    )}
                                                </td>
                                                {isEditing && (
                                                    <td className="text-center">
                                                        <Button variant="link" className="text-danger p-0" onClick={() => handleRemoveItem(idx)}>
                                                            <FaTrash size={14} />
                                                        </Button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                {isEditing && (
                                    <div className="p-3 border-top bg-light">
                                        <Button variant="outline-primary" size="sm" onClick={handleAddItem}>
                                            <FaPlus /> Add Line
                                        </Button>
                                    </div>
                                )}
                            </Card>
                        </div>
                    </>
                ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                        <div className="bg-light p-4 rounded-circle mb-3">
                            <FaFileInvoiceDollar size={48} style={{ color: SOURCING_ACCENT }} />
                        </div>
                        <h5>Select an RFQ to view details</h5>
                        <p className="small">Or create a new one to start sourcing.</p>
                        <Button variant="primary" onClick={handleNewRFQ} style={{ backgroundColor: SOURCING_ACCENT, borderColor: SOURCING_ACCENT }}>
                            Create New RFQ
                        </Button>
                    </div>
                )}
            </Col>

            <style>{`
                .hover-bg-light:hover { background-color: #f8f9fa; }
                .cursor-pointer { cursor: pointer; }
                /* Custom Scrollbar */
                ::-webkit-scrollbar { width: 6px; height: 6px; }
                ::-webkit-scrollbar-track { background: #f1f1f1; }
                ::-webkit-scrollbar-thumb { background: #ccc; border-radius: 3px; }
                ::-webkit-scrollbar-thumb:hover { background: #999; }
            `}</style>
        </Row>
    );
};

export default RFQManager;
